import React, { useEffect, useRef } from "react"

import { StyledListItem, StyledSubListItem } from "./styles"
import { Box, Link, Typography, useTheme } from "@mui/material"
import Icon from "@mdi/react"
import { ListTextItemTypes } from "../types"
import { mdiChevronDown, mdiChevronUp } from "@mdi/js"
import { isValidUrl } from "pages/util"
import {
  companyAddressIcon,
  companyEmailIcon,
  companyNameIcon,
  companyPhoneIcon,
  companyWebsiteIcon,
} from "utils/Global"

const companyIcons = {
  name2: companyNameIcon,
  street: companyAddressIcon,
  city: companyAddressIcon,
  zip: companyAddressIcon,
  mailbox: companyEmailIcon,
  wwwURL: companyWebsiteIcon,
  phone: companyPhoneIcon,
  fax: companyPhoneIcon,
  email: companyEmailIcon,
}

const ShowButton = (props) => {
  const { isOpen, setIsOpen } = props
  return (
    <Box
      onClick={() => setIsOpen(!isOpen)}
      sx={{ alignItems: "center", display: "flex" }}
    >
      <Icon
        path={isOpen ? mdiChevronUp : mdiChevronDown}
        size={"24px"}
        style={{ marginLeft: "auto", color: "black" }}
      />
    </Box>
  )
}

const ListTextItem: React.FC<ListTextItemTypes> = (props) => {
  const {
    subItems,
    hasBorder,
    hasPadding,
    labelWidth,
    updateLabelWidth,
    onClick,
    hidden,
    show: initialShow = true,
    labelIcon,
    lastItem,
    from,
    fromKey,
  } = props
  const theme = useTheme()
  const [show, setShow] = React.useState(initialShow)

  return (
    <>
      <StyledListItem
        className="list-item"
        border={hasBorder ? `1px solid #eee` : ""}
        borderTop={"0"}
        hasPadding={hasPadding}
        padding={
          subItems.length === 1 ? "4px 0" : hasBorder ? "11px" : "11px 0"
        }
        borderRadius={lastItem && hasBorder ? "0 0 4px 4px" : "0"}
        onClick={onClick}
        theme={theme}
        sx={{ gap: theme.spacing(1), marginTop: labelIcon ? "4px" : "" }}
      >
        {subItems.map((item, index) => {
          return (
            <React.Fragment key={fromKey + item.label + index}>
              {item.separator && (item.hasBoldLabel || show) && (
                <div
                  style={{
                    borderBottom: `1px solid ${theme.palette.grey.A200}`,
                    marginBlock: theme.spacing(1),
                  }}
                />
              )}
              <StyledSubListItem
                className="sub-list-item"
                justifyContent={"flex-start"}
                gap={
                  item.hasBoldLabel
                    ? theme.spacing(3)
                    : labelIcon
                    ? theme.spacing(1)
                    : "0"
                }
                sx={{
                  justifyContent: labelIcon ? "flex-start" : "space-between",
                  flexDirection:
                    labelIcon || item.hasBoldLabel ? "row" : "column",
                  "&:empty": {
                    display: "none",
                  },
                  "&:not(empty):not(nth-child(1)):last-child": {
                    marginBottom: "16px",
                  },
                }}
                onClick={() => {
                  if (item.hasBoldLabel) setShow(!show)
                }}
              >
                {/* Country selection section */}
                {item.label &&
                !item.hasBoldLabel &&
                !item.separator &&
                !item.content &&
                from !== "info" ? (
                  <Box
                    sx={{
                      padding: "11px",
                      border: "1px solid #eee",
                      margin: "-9px 0 0",
                      borderRadius: lastItem ? "0 0 4px 4px" : "0",
                    }}
                  >
                    <Typography>{item.label}</Typography>
                  </Box>
                ) : (
                  <>
                    {/* Others section */}
                    {labelIcon ? (
                      <img src={companyIcons[labelIcon]} alt={item.label} />
                    ) : (
                      (item.hasBoldLabel || show) &&
                      !item.separator && (
                        <ListTextItemLabel
                          fromKey={fromKey}
                          hidden={hidden}
                          item={item}
                          labelWidth={labelWidth}
                          updateLabelWidth={updateLabelWidth}
                        />
                      )
                    )}
                    {item.content &&
                      show &&
                      (isValidUrl(item.content) ? (
                        <Box sx={{ display: "flex" }}>
                          {item.content.includes("@") &&
                          item.content.length > 28 ? (
                            <Link href={`mailto:${item.content}`}>
                              {item.content}
                            </Link>
                          ) : item.content.includes("www.") &&
                            item.content.length > 28 ? (
                            <Link
                              href={`${
                                !(
                                  item.content.startsWith("http://") ||
                                  item.content.startsWith("https://")
                                )
                                  ? "https://"
                                  : ""
                              }${item.content}`}
                              target="_blank"
                              rel="noreferrer"
                            >
                              {item.content}
                            </Link>
                          ) : (
                            <Link
                              href={`${
                                !(
                                  item.content.startsWith("http://") ||
                                  item.content.startsWith("https://")
                                )
                                  ? "https://"
                                  : ""
                              }${item.content}`}
                            >
                              {item.content}
                            </Link>
                          )}
                        </Box>
                      ) : (
                        <Typography
                          className="list-text-item-content"
                          onClick={() => {}}
                          style={{
                            fontSize:
                              item.label && item.content ? "14px" : "16px",
                            fontWeight: 700,
                          }}
                        >
                          {item.content}
                        </Typography>
                      ))}
                    {item.hasBoldLabel && (
                      <ShowButton isOpen={show} setIsOpen={setShow} />
                    )}
                  </>
                )}
              </StyledSubListItem>
            </React.Fragment>
          )
        })}
      </StyledListItem>
    </>
  )
}

const ListTextItemLabel = (props) => {
  const ref = useRef(null)
  const { item, labelWidth, updateLabelWidth, hidden, fromKey } = props

  useEffect(() => {
    // Add 1 to make sure values rounded down are not cut off
    if (!hidden && !labelWidth && !item?.autoWidth) {
      updateLabelWidth(ref.current ? ref.current.offsetWidth + 1 : 0)
    }
    // eslint-disable-next-line
  }, [hidden])

  return (
    <Box
      className="list-text-item-label"
      sx={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
      }}
    >
      {(item.label as string).split("/").map((value, index) => (
        <Typography
          key={fromKey + value + index}
          sx={{
            fontWeight: item.hasBoldLabel ? "bold" : "normal",
            flexBasis: "auto",
            fontSize: item.label && item.content ? "14px" : "16px",
          }}
          variant={"body1"}
          ref={ref}
        >
          {value}
        </Typography>
      ))}
    </Box>
  )
}
export default ListTextItem
